import { LockFilled } from "@ant-design/icons";
import { Badge, Card, Col, Divider, Progress, Row, Tooltip, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import BetaInvite from "../components/BetaInvite";
import ExperiencePrompt from "./ExperiencePrompt";
import styles from "./Dashboard.module.css";


const { Title } = Typography;

const LearningJourneyCard = ({ learningJourney, onClick, animationDelay = 0 }) => {
    const { locked, title, description, progress } = learningJourney;

    const handleKeyDown = (event) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onClick();
        }
    };

    return (
        <Tooltip
            mouseEnterDelay={0.3}
            classNames={{
                body: styles.tooltipBody
            }}
            color="rgba(255, 255, 255, 0.95)"
            title={description}
        >
            <Card
                hoverable
                onClick={onClick}
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={0}
                aria-label={locked ? `${title}, locked` : title}
                aria-haspopup={locked ? "dialog" : undefined}
                className={styles.learnJourneyItem}
                style={{ animationDelay: `${animationDelay}s` }}
                variant="borderless"
            >
                <Title level={4}>
                    {locked && <LockFilled aria-hidden="true" className={styles.lockGlyph} />}
                    {title}
                </Title>

                <Progress
                    percent={progress}
                    strokeColor={{ "0%": "var(--brand-light)", "100%": "var(--brand)" }}
                    size={{ "width": "100%", "height": "8px" }}
                    showInfo={false}
                    status={progress < 100 ? "active" : "normal"}
                />
            </Card>
        </Tooltip>
    );
}


export const Dashboard = () => {
    const navigate = useNavigate();
    const [learningJourneys, setLearningJourneys] = useState([]);
    const [pageStart, setPageStart] = useState(0);
    const [nextCursor, setNextCursor] = useState(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [experiencePrompt, setExperiencePrompt] = useState({ due: false, journeyTitle: "" });
    const [loading, setLoading] = useState(false);
    const [betaInviteJourneyId, setBetaInviteJourneyId] = useState(null);
    const sentinelRef = useRef(null);

    const loadPage = async (cursor) => {
        const setBusy = cursor ? setLoadingMore : setLoading;
        setBusy(true);
        try {
            const { data } = await api.get("/workspace/dashboard/", {
                params: cursor ? { cursor } : {},
            });
            setLearningJourneys((prev) => {
                setPageStart(cursor ? prev.length : 0);
                return cursor ? [...prev, ...data.journeys] : data.journeys;
            });
            setNextCursor(data.next);
            if (!cursor) {
                setExperiencePrompt({
                    due: data.experience_prompt.due,
                    journeyTitle: data.experience_prompt.journey_title,
                });
            }
        } catch (error) {
            console.error("Error fetching dashboard:", error);
        } finally {
            setBusy(false);
        }
    };

    const startNewLearningJourney = async () => {
        try {
            const { data } = await api.post("/workspace/new-learning-journey/");
            if (data.learning_journey_id) {
                navigate(`/t/${data.learning_journey_id}`);
            }
        } catch (error) {
            console.error("Error starting new learningJourney:", error);
        }
    };

    const openJourney = (lj) => {
        if (lj.locked) {
            setBetaInviteJourneyId(lj.id);
        } else {
            navigate(`/t/${lj.id}`);
        }
    };

    useEffect(() => {
        loadPage(null);
    }, []);

    useEffect(() => {
        if (!sentinelRef.current || !nextCursor || loadingMore) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) loadPage(nextCursor);
            },
            { rootMargin: "400px 0px" },
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [nextCursor, loadingMore]);

    const ready = !loading;

    const ongoingLearningJourney = learningJourneys.find((t) => !t.completed && t.progress < 100);

    // Exclude ongoing learningJourney from grid list
    const gridLearningJourneys = ongoingLearningJourney
        ? learningJourneys.filter((t) => t.id !== ongoingLearningJourney.id)
        : learningJourneys;

    const cardColLayout = {
        xs: 24, // 1 card per row on phones
        sm: 24, // 1 card per row on small tablets/landscape phones
        md: 12, // 2 cards per row on tablets
        lg: 8,  // 3 cards per row on desktop (original size)
    };

    return (
        <div>
            <Row gutter={[16, 16]}
                justify="center"
                align="middle"
            >
                {/* Ongoing LearningJourney Card */}
                {ongoingLearningJourney && (
                    <Col {...cardColLayout} span={8}>
                        <div className={styles.hoverBox}>
                            <LearningJourneyCard
                                learningJourney={ongoingLearningJourney}
                                onClick={() => openJourney(ongoingLearningJourney)}
                            />
                        </div>
                    </Col>
                )}

                {/* Start New LearningJourney Card */}
                <Col {...cardColLayout} span={8}>
                    <Card hoverable
                        className={styles.startNewCard}
                        onClick={startNewLearningJourney}
                    >
                        <Title level={4} style={{ color: "var(--text-light)" }}>
                            Start a New Learning Journey
                        </Title>
                        <div style={{ visibility: "hidden" }}>
                            <Progress percent={0} status="active" />
                        </div>
                    </Card>
                </Col>
            </Row>

            <ExperiencePrompt due={experiencePrompt.due} journeyTitle={experiencePrompt.journeyTitle} />

            <Divider />

            {/* LearningJourneys grid */}
            <Row gutter={[16, 16]} style={{ overflowY: "visible", maxHeight: "70vh" }}>
                {!ready ? (
                    Array.from({ length: 3 }).map((_, index) => (
                        <Col {...cardColLayout} span={8} key={index}>
                            <Card loading className={styles.learnJourneyItem} />
                        </Col>
                    ))
                ) : (
                    gridLearningJourneys.map((lj, index) => (
                        <Col {...cardColLayout} span={8} key={lj.id}>
                            <div className={styles.hoverBox}>
                                {lj.completed ? (
                                    <Badge.Ribbon text="Completed" color="green">
                                        <LearningJourneyCard
                                            learningJourney={lj}
                                            onClick={() => openJourney(lj)}
                                            animationDelay={Math.min(Math.max(index - pageStart, 0), 6) * 0.06}
                                        />
                                    </Badge.Ribbon>
                                ) : (
                                    <LearningJourneyCard
                                        learningJourney={lj}
                                        onClick={() => openJourney(lj)}
                                        animationDelay={Math.min(Math.max(index - pageStart, 0), 6) * 0.06}
                                    />
                                )}
                            </div>
                        </Col>
                    ))
                )}

                {ready && loadingMore &&
                    Array.from({ length: 3 }).map((_, index) => (
                        <Col {...cardColLayout} span={8} key={`loading-more-${index}`}>
                            <Card loading className={styles.learnJourneyItem} />
                        </Col>
                    ))
                }

                {ready && nextCursor && <div ref={sentinelRef} style={{ height: 1 }} />}
            </Row>

            <BetaInvite
                open={betaInviteJourneyId !== null}
                join={() =>
                    api.post("/workspace/roadmap-calibration/join-beta-and-start/", {
                        learning_journey_id: betaInviteJourneyId,
                    })
                }
                onJoined={() => navigate(`/t/${betaInviteJourneyId}`)}
                onDismissed={() => setBetaInviteJourneyId(null)}
            />
        </div>
    );
}

export default Dashboard;